import { generateURNByKind } from '@agilelab/plugin-wb-builder-common';
import {
  AsyncEnumFilter,
  ProviderFilters,
  SearchFilter,
  WbTableFilters,
} from '@agilelab/plugin-wb-platform';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { FC, useCallback } from 'react';
import useDataContractContext from './DataContractProvider';

export interface DataContractFilters extends ProviderFilters {
  system?: { name: string; urn: string }[];
}

enum AvailableDataContractFilters {
  search = 'search',
  system = 'system',
}

export interface SystemFilterProps {
  value: { name: string; urn: string }[];
  changeFilters: <K extends keyof DataContractFilters>(
    key: K,
    value: DataContractFilters[K],
  ) => void;
  getOptions: () => Promise<{ name: string; urn: string }[]>;
}

export const SystemFilter: FC<SystemFilterProps> = ({
  value,
  changeFilters,
  getOptions,
}) => {
  return (
    <AsyncEnumFilter<{ name: string; urn: string }>
      field="System"
      value={value ?? undefined}
      onChange={system => {
        changeFilters('system', system);
      }}
      renderOption={o => o.name}
      renderValue={v => v.name}
      onSearch={(searchValue, options) =>
        options.filter(o => new RegExp(searchValue, 'ig').test(o.name))
      }
      getOptions={getOptions}
    />
  );
};

export const DataContractFiltersSection = () => {
  const { filters, changeFilters, resetFilters, templateKind } =
    useDataContractContext();

  const catalogApi = useApi(catalogApiRef);

  const fetchAvailableSystems = useCallback(async () => {
    return catalogApi
      .getEntities({ filter: { kind: 'system' } })
      .then(response =>
        response.items.map(item => {
          const urn = generateURNByKind(item.metadata.name, item.kind);
          return { name: item.metadata.name, urn: urn };
        }),
      );
  }, [catalogApi]);

  const filterElement = (key: string) => {
    if (key === AvailableDataContractFilters.search)
      return (
        <SearchFilter
          key={key}
          value={filters.search ?? ''}
          changeFilters={changeFilters}
        />
      );

    if (
      key === AvailableDataContractFilters.system &&
      !templateKind?.filter?.system
    )
      return (
        <SystemFilter
          key={key}
          value={filters.system ?? []}
          changeFilters={changeFilters}
          getOptions={fetchAvailableSystems}
        />
      );

    return <></>;
  };

  return (
    <WbTableFilters onClear={resetFilters}>
      {templateKind?.userFilters?.map(filter => filterElement(filter))}
    </WbTableFilters>
  );
};
